import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import {
  AgricolaDependentDialogData,
  InsuranceDependent
} from '../../../../../interfaces/agricola.interface';
import { SisSelectOption } from '../../../../sis/sis-select/sis-select.component';
import {
  AGRICOLA_MINOR_IDENTIFICATION_TYPE,
  agricolaIsMinor,
  agricolaToIsoDate
} from '../../../agricola-validators';

// 'pending' hasta que se captura una fecha de nacimiento utilizable; a partir
// de ahí el documento se captura (mayor) o se genera del titular (menor).
type AgricolaIdentificationMode = 'pending' | 'minor' | 'adult';

@Component({
  selector: 'app-agricola-dependent-dialog',
  templateUrl: './agricola-dependent-dialog.component.html',
  styleUrls: ['./agricola-dependent-dialog.component.css'],
  standalone: false
})
export class AgricolaDependantDialogComponent implements OnInit, OnDestroy {

  form!: FormGroup;
  showErrorBanner = false;
  // Birthday must be strictly in the past: latest selectable day is yesterday,
  // which also caps the datepicker calendar.
  readonly maxBirthday: Date = this.buildYesterday();
  relationshipOptions: SisSelectOption[] = [];
  relationshipLoading = false;
  relationshipLoadedKey = 0;
  relationshipLoadFailed = false;
  private readonly subscriptions = new Subscription();
  // Last applied identification mode; null until the form is initialised.
  private identificationMode: AgricolaIdentificationMode | null = null;

  readonly genderOptions: SisSelectOption[] = [
    { label: 'Masculino', value: '0' },
    { label: 'Femenino', value: '1' }
  ];

  readonly identificationTypeOptions: SisSelectOption[] = [
    { label: 'Número único', value: '1' },
    { label: 'DUI', value: '2' },
    { label: 'NIT', value: '3' },
    { label: 'Carnet residente', value: '4' },
    { label: 'Pasaporte', value: '5' }
  ];

  // Identification-type codes (mirror identificationTypeOptions values).
  private static readonly ID_TYPE = {
    uniqueNumber: '1',
    dui: '2',
    nit: '3',
    residentCard: '4',
    passport: '5'
  } as const;

  // Names: letters (any language, accents/ñ included), spaces and the usual
  // name punctuation (apostrophe, hyphen, dot). Must start with a letter and
  // reject digits or other symbols.
  private static readonly NAME_PATTERN = /^\p{L}[\p{L}\p{M}'’.\-\s]*$/u;

  // Matches the spouse entries of the relationship catalog (Esposo, Esposa,
  // Conyugue/Cónyuge) once the description is normalised.
  private static readonly SPOUSE_PATTERN = /^(espos[oa]|conyug)/;
  readonly nameMaxLength = 50;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AgricolaDependantDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AgricolaDependentDialogData
  ) {}

  ngOnInit(): void {
    this.relationshipOptions = this.data.relationshipOptions;
    this.bindRelationshipCatalogState();
    const d = this.data.dependent;
    this.form = this.fb.group({
      first_name: [d?.first_name || '', [Validators.required, this.nameValidator(), Validators.maxLength(this.nameMaxLength)]],
      middle_name: [d?.middle_name || '', [this.nameValidator(), Validators.maxLength(this.nameMaxLength)]],
      last_name: [d?.last_name || '', [Validators.required, this.nameValidator(), Validators.maxLength(this.nameMaxLength)]],
      last_name_2: [d?.last_name_2 || '', [this.nameValidator(), Validators.maxLength(this.nameMaxLength)]],
      gender: [d?.gender != null ? String(d.gender) : '', Validators.required],
      relationship: [d?.relationship || '', [Validators.required, this.spouseNotDuplicatedValidator()]],
      identification_type: [d?.identification_type == null ? '' : String(d.identification_type), Validators.required],
      identification_id: [
        d?.identification_id || '',
        [
          Validators.required,
          this.identificationNotDuplicatedValidator(),
          this.identificationNotInsuredValidator(),
          this.identificationFormatValidator()
        ]
      ],
      birthday: [
        d?.birthday || '',
        [Validators.required, this.birthdayNotFutureValidator(), this.birthdayMaxAgeValidator()]
      ]
    });

    // The identification format depends on the selected type, so re-validate the
    // number whenever the type changes.
    this.subscriptions.add(
      this.form.get('identification_type')!.valueChanges.subscribe(() => {
        this.form.get('identification_id')?.updateValueAndValidity();
      })
    );

    // Minors do not capture a document: it is generated from the holder's, so
    // the identification controls are hidden and stop being required.
    this.subscriptions.add(
      this.form.get('birthday')!.valueChanges.subscribe(() => this.syncIdentificationControls())
    );
    this.syncIdentificationControls();
  }

  // True when the birthday entered belongs to someone under 18 today.
  get isMinor(): boolean {
    return agricolaIsMinor(this.form?.get('birthday')?.value);
  }

  // Los campos de documento solo aparecen cuando hay una fecha de nacimiento
  // utilizable y corresponde a un mayor de edad: antes de eso no se sabe si el
  // documento se captura o se genera.
  get showIdentificationFields(): boolean {
    return this.identificationModeFor() === 'adult';
  }

  private identificationModeFor(): AgricolaIdentificationMode {
    const control = this.form?.get('birthday');
    if (!control || !/^\d{4}-\d{2}-\d{2}$/.test(this.toIsoDate(control.value)) || control.invalid) {
      return 'pending';
    }

    return this.isMinor ? 'minor' : 'adult';
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // Max characters allowed for the number, sized to the selected id type so the
  // native maxlength stops obviously-too-long input.
  get identificationMaxLength(): number {
    switch (String(this.form?.get('identification_type')?.value || '')) {
      case AgricolaDependantDialogComponent.ID_TYPE.dui:
        return 10; // 8 digits + hyphen + check digit
      case AgricolaDependantDialogComponent.ID_TYPE.nit:
        return 17; // 14 digits + 3 hyphens
      case AgricolaDependantDialogComponent.ID_TYPE.uniqueNumber:
        return 15;
      default:
        return 20; // resident card / passport
    }
  }

  nameError(controlName: string, label: string): string {
    const control = this.form?.get(controlName);
    if (!control || !control.invalid || !control.touched) {
      return '';
    }

    if (control.hasError('required')) {
      return `Ingresa ${label}.`;
    }

    if (control.hasError('invalidName')) {
      return 'Solo se permiten letras, espacios y los signos \' - .';
    }

    if (control.hasError('maxlength')) {
      return `Máximo ${this.nameMaxLength} caracteres.`;
    }

    return '';
  }

  get identificationError(): string {
    const control = this.form?.get('identification_id');
    if (!control || !control.invalid || !control.touched) {
      return '';
    }

    if (control.hasError('duplicateIdentification')) {
      return 'Este número de identificación ya fue agregado.';
    }

    if (control.hasError('insuredIdentification')) {
      return 'El dependiente no puede ser el asegurado.';
    }

    if (control.hasError('required')) {
      return 'Ingresa número de identificación.';
    }

    if (control.hasError('invalidDui')) {
      return 'El DUI debe tener 8 dígitos y un dígito verificador (ej. 01234567-8).';
    }

    if (control.hasError('invalidNit')) {
      return 'El NIT debe tener 14 dígitos (ej. 0614-123456-101-2).';
    }

    if (control.hasError('invalidUniqueNumber')) {
      return 'El número único solo admite dígitos.';
    }

    if (control.hasError('invalidDocument')) {
      return 'Número de identificación inválido.';
    }

    return 'Ingresa número de identificación.';
  }

  get birthdayError(): string {
    const control = this.form?.get('birthday');
    if (!control || !control.invalid || !control.touched) {
      return '';
    }

    if (control.hasError('futureBirthday')) {
      return 'La fecha de nacimiento debe ser anterior a hoy.';
    }

    if (control.hasError('ageTooOld')) {
      return 'La edad no puede superar los 120 años.';
    }

    return 'Ingresa fecha de nacimiento.';
  }

  get relationshipError(): string {
    const control = this.form?.get('relationship');
    if (!control || !control.invalid || !control.touched) {
      return '';
    }

    if (control.hasError('duplicateSpouse')) {
      return 'Ya agregaste un cónyuge; solo se permite uno por póliza.';
    }

    return 'Selecciona parentesco.';
  }

  get relationshipCaption(): string {
    return this.relationshipOptions.length ? `${this.relationshipOptions.length} opciones` : '';
  }

  save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.showErrorBanner = true;
      return;
    }
    this.showErrorBanner = false;
    const value = this.form.value;
    const dependent: InsuranceDependent = {
      first_name: String(value.first_name || '').trim(),
      middle_name: String(value.middle_name || '').trim(),
      last_name: String(value.last_name || '').trim(),
      last_name_2: String(value.last_name_2 || '').trim(),
      gender: value.gender === '' ? undefined : Number(value.gender),
      relationship: String(value.relationship || ''),
      identification_id: String(value.identification_id || '').trim(),
      identification_type: value.identification_type === '' ? null : Number(value.identification_type),
      birthday: this.toIsoDate(value.birthday) || String(value.birthday || '').trim()
    };

    dependent.name = [dependent.first_name, dependent.middle_name, dependent.last_name, dependent.last_name_2]
      .filter(Boolean)
      .join(' ')
      .trim();

    this.dialogRef.close(dependent);
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  retryRelationship(): void {
    this.data.retryRelationship?.();
  }

  // Switches the document controls between hidden (no usable birthday yet),
  // captured (adults) and generated (minors). For minors only the type is kept
  // here; the number depends on the dependant position within the policy, so
  // the step assigns it.
  private syncIdentificationControls(): void {
    const mode = this.identificationModeFor();
    if (mode === this.identificationMode) {
      return;
    }

    const wasMinor = this.identificationMode === 'minor';
    this.identificationMode = mode;
    const type = this.form.get('identification_type')!;
    const id = this.form.get('identification_id')!;

    if (mode === 'pending') {
      // Los campos están ocultos: sin validadores, para que el único error
      // pendiente sea el de la propia fecha de nacimiento.
      type.clearValidators();
      id.clearValidators();
      if (wasMinor) {
        type.setValue('', { emitEvent: false });
        id.setValue('', { emitEvent: false });
      }
    } else if (mode === 'minor') {
      type.clearValidators();
      type.setValue(String(AGRICOLA_MINOR_IDENTIFICATION_TYPE), { emitEvent: false });
      id.clearValidators();
      id.setValue('', { emitEvent: false });
    } else {
      type.setValidators([Validators.required]);
      id.setValidators([
        Validators.required,
        this.identificationNotDuplicatedValidator(),
        this.identificationNotInsuredValidator(),
        this.identificationFormatValidator()
      ]);
      // Coming back from a minor birthday the values were generated, not typed.
      if (wasMinor) {
        type.setValue('', { emitEvent: false });
        id.setValue('', { emitEvent: false });
      }
    }

    type.updateValueAndValidity({ emitEvent: false });
    id.updateValueAndValidity({ emitEvent: false });
  }

  private bindRelationshipCatalogState(): void {
    if (this.data.relationshipOptions$) {
      this.subscriptions.add(this.data.relationshipOptions$.subscribe(options => {
        this.relationshipOptions = options;
        // The spouse check reads the option labels, so it can only be resolved
        // once the catalog arrives.
        this.form?.get('relationship')?.updateValueAndValidity({ emitEvent: false });
      }));
    }

    if (this.data.relationshipLoading$) {
      this.subscriptions.add(this.data.relationshipLoading$.subscribe(loading => {
        this.relationshipLoading = loading;
      }));
    }

    if (this.data.relationshipLoadedKey$) {
      this.subscriptions.add(this.data.relationshipLoadedKey$.subscribe(loadedKey => {
        this.relationshipLoadedKey = loadedKey;
      }));
    }

    if (this.data.relationshipLoadFailed$) {
      this.subscriptions.add(this.data.relationshipLoadFailed$.subscribe(loadFailed => {
        this.relationshipLoadFailed = loadFailed;
      }));
    }
  }

  // Accepts only letters (accents/ñ included), spaces and name punctuation.
  // Empty values are left to the required validator so optional names stay valid.
  private nameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = String(control.value || '').trim();
      if (!value) {
        return null;
      }
      return AgricolaDependantDialogComponent.NAME_PATTERN.test(value) ? null : { invalidName: true };
    };
  }

  // Validates the identification number against the selected id type. The type
  // lives on a sibling control, so it is read through control.parent.
  private identificationFormatValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = String(control.value || '').trim();
      const type = String(control.parent?.get('identification_type')?.value || '');
      if (!raw || !type) {
        return null;
      }

      const ID_TYPE = AgricolaDependantDialogComponent.ID_TYPE;
      switch (type) {
        case ID_TYPE.dui:
          // 8 digits + check digit, hyphen optional (01234567-8 or 012345678).
          return /^\d{8}-?\d$/.test(raw) ? null : { invalidDui: true };
        case ID_TYPE.nit:
          // 14 digits, hyphens optional (0614-123456-101-2 or 06141234561012).
          return /^\d{4}-?\d{6}-?\d{3}-?\d$/.test(raw) ? null : { invalidNit: true };
        case ID_TYPE.uniqueNumber:
          return /^\d{1,15}$/.test(raw) ? null : { invalidUniqueNumber: true };
        case ID_TYPE.residentCard:
        case ID_TYPE.passport:
          // Alphanumeric documents (optionally hyphenated).
          return /^[A-Za-z0-9-]{5,20}$/.test(raw) ? null : { invalidDocument: true };
        default:
          return null;
      }
    };
  }

  // Only one spouse (esposo / esposa / cónyuge) is allowed per policy: the
  // relationship catalog lists them as separate codes, so they are matched by
  // description instead of by code (codes differ between catalogs).
  private spouseNotDuplicatedValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.isSpouseRelationship(control.value)) {
        return null;
      }

      const taken = (this.data.existingRelationships || []).some(code => this.isSpouseRelationship(code));
      return taken ? { duplicateSpouse: true } : null;
    };
  }

  private isSpouseRelationship(code: string | number | null | undefined): boolean {
    const value = code == null ? '' : String(code);
    if (!value) {
      return false;
    }

    const label = this.relationshipOptions.find(option => String(option.value) === value)?.label || '';
    return AgricolaDependantDialogComponent.SPOUSE_PATTERN.test(this.normalizeText(label));
  }

  // Lowercased and accent-free, so 'Cónyuge' / 'Conyugue' / 'CONYUGE' all match.
  private normalizeText(value: string): string {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  }

  private identificationNotDuplicatedValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = this.normalizeId(control.value);
      if (!value) {
        return null;
      }

      const existing = (this.data.existingIdentificationIds || []).map(id => this.normalizeId(id));
      return existing.includes(value) ? { duplicateIdentification: true } : null;
    };
  }

  private identificationNotInsuredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = this.normalizeId(control.value);
      const insured = this.normalizeId(this.data.insuredIdentificationId);
      return value && insured && value === insured ? { insuredIdentification: true } : null;
    };
  }

  // Rejects a birthday that falls on today or in the future. Empty/unparseable
  // values are left to the required validator so this only flags real dates.
  private birthdayNotFutureValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const iso = this.toIsoDate(control.value);
      const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
      if (!parts) {
        return null;
      }

      const birth = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return birth.getTime() >= today.getTime() ? { futureBirthday: true } : null;
    };
  }

  // Rejects an implausibly old birthday (more than 120 years ago). Empty or
  // unparseable values are left to the required validator.
  private birthdayMaxAgeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const iso = this.toIsoDate(control.value);
      const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
      if (!parts) {
        return null;
      }

      const birth = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
      const oldest = new Date();
      oldest.setHours(0, 0, 0, 0);
      oldest.setFullYear(oldest.getFullYear() - 120);
      return birth.getTime() < oldest.getTime() ? { ageTooOld: true } : null;
    };
  }

  private buildYesterday(): Date {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - 1);
    return date;
  }

  private normalizeId(value: unknown): string {
    return String(value || '').trim().toUpperCase();
  }

  private toIsoDate(value: unknown): string {
    return agricolaToIsoDate(value);
  }
}
